import { IOption } from '@/interfaces/store';
import { BasicSelect } from '@/components/BasicSelect';
import useDeviceDetect from '@/hooks/deviceDetect';

interface LocationSelectorsProps {
  districts: IOption[];
  thanas: IOption[];
  districtId: string;
  thanaId: string;
  onDistrictChange: (value: string) => void;
  onThanaChange: (value: string) => void;
  hideThana?: boolean;
}

/** Widens the react-select popup beyond the trigger on mobile. The global
 *  Menu override pins popup width to trigger width with `!important`, so a
 *  CSS-only `@media` block can't override it — inline styles via BasicSelect
 *  are the only way through. */
const withWrappedOption = (base: any) => ({
  ...base,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
});

const getLocationMenuStyles = (name: 'district' | 'thana', isMobile: boolean) => {
  if (!isMobile) {
    // Desktop: let react-select use its trigger-pinned defaults; only
    // thana keeps its long-name wrap (Bangla place names render here too).
    return { option: name === 'thana' ? withWrappedOption : undefined };
  }

  const configs = {
    district: { width: 'calc(200% + 8px)', left: '0px' },
    thana: { width: 'calc(200% + 8px)', left: 'calc(-100% - 8px)' },
  } as const;

  const config = configs[name];

  return {
    menu: (base: any) => ({
      ...base,
      width: config.width,
      minWidth: config.width,
      maxWidth: 'none',
      left: config.left,
      right: 'auto',
      transform: 'none',
    }),
    menuList: (base: any) => ({ ...base, minWidth: 0, maxWidth: 'none' }),
    option: withWrappedOption,
  };
};

/** Dropdown row for District + Thana. On mobile the popup menu widens
 *  beyond the trigger so long Bangla place names don't clip. */
export const LocationSelectors = ({
  districts,
  thanas,
  districtId,
  thanaId,
  onDistrictChange,
  onThanaChange,
  hideThana = false,
}: LocationSelectorsProps) => {
  const { isMobile } = useDeviceDetect();

  return (
    <>
      <div className="mt-2 flex justify-start gap-2">
        <div className="flex-1 text-left">
          <BasicSelect
            className="react-select-value-left my-auto w-full"
            name="district"
            placeholder="All Districts"
            searchable={true}
            unstyled
            onChange={(e: any) => onDistrictChange(e.value)}
            options={districts}
            value={districtId}
            styles={getLocationMenuStyles('district', isMobile)}
          />
        </div>
        {!hideThana && (
          <div className="thana-trigger min-w-0 flex-1 text-left">
            <BasicSelect
              className="react-select-value-left my-auto w-full"
              name="thana"
              placeholder="All Thanas"
              searchable={true}
              unstyled
              onChange={(e: any) => onThanaChange(e.value)}
              options={thanas}
              value={thanaId}
              styles={getLocationMenuStyles('thana', isMobile)}
            />
          </div>
        )}
      </div>
      <style jsx global>{`
        .react-select-value-left .select-container .control {
          box-sizing: border-box;
          display: flex;
          flex-direction: row;
          align-items: center;
          padding: 10px 12px;
          gap: 8px;
          height: 37px;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          background: #fff;
        }
        .react-select-value-left .select-container .control > div {
          font-family: 'Figtree', system-ui, sans-serif;
          font-style: normal;
          font-weight: 400;
          font-size: 14px;
          line-height: 120%;
          color: #000;
        }

        /* iOS Safari body-scroll-lock for the District/Thana popup.
         *
         * The mobile popup is width: calc(200% + 8px) to fit long Bangla
         * names; on iPhone SE (320 CSS px) this overflows the viewport by a
         * few pixels. iOS Safari ignores body { overflow-x: hidden } for
         * the document scrolling element, so a wide descendant surfaces a
         * body-level horizontal scrollbar -- the modal then appears
         * "zoomed in" because the body has scrolled while the modal
         * (positioned with fixed inset-0) stays pinned to the viewport.
         *
         * Pinning the body with position: fixed and width: 100% while any
         * react-select menu is in the DOM keeps iOS from scrolling the
         * body. The :has pseudo-class is supported in Safari 15.4+ (Apr 2022)
         * and all evergreen browsers, so the rule degrades silently on older
         * engines (where the original iOS bug would still surface, but
         * the rule causes no other side effects). The rule is scoped to
         * the LocationSelectors component via its style-jsx global block,
         * so it does not affect any other modal in the app.
         */
        body:has(.react-select__menu) {
          position: fixed;
          width: 100%;
          left: 0;
          right: 0;
          overflow-x: hidden;
        }
      `}</style>
    </>
  );
};

export default LocationSelectors;